import type { ReactNode } from 'react';
import styles from './field.module.css';
import { cx } from './cx';

/**
 * A labelled text input. Renders props.
 *
 * The label is always **present**, and `hideLabel` only hides it visually — a placeholder is not a
 * label (it disappears the moment someone types, and a screen reader may never read it at all). The
 * header's search box is the case that wants this: there is no room for "Search" above it, and there
 * is every reason for the field to have a name.
 *
 * `problem` is wired through `aria-describedby` rather than just rendered underneath, so the reason a
 * value was refused is read out with the field instead of being a sentence someone has to go find.
 *
 * `combobox` turns it into §8's suggestions box. It is a prop group rather than a second
 * component because the *field* is unchanged — same markup, same styling, same label rule — and what a
 * combobox adds is four ARIA attributes and a key hook. Building a second input to carry them would
 * duplicate the one thing worth having in one place.
 */
export interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit?: () => void;
  readonly placeholder?: string;
  readonly hideLabel?: boolean;
  readonly type?: 'text' | 'search';
  readonly problem?: string | null;
  readonly autoComplete?: string;
  readonly spellCheck?: boolean;
  /** A button beside the input — "Watch", "Search". */
  readonly trailing?: ReactNode;
  /**
   * §8's suggestions list, and the ARIA that makes it one. `activeId` is the highlighted option's DOM
   * id: `aria-activedescendant` is how a combobox says "this row is current" **without moving focus**,
   * which is what keeps the caret in the text while `↓` walks the list.
   */
  readonly combobox?: {
    readonly expanded: boolean;
    readonly listId: string;
    readonly activeId: string | null;
  };
  /**
   * Returns `true` when it claimed the key. Consulted *before* Enter-submits, so a combobox can make
   * Enter mean "take the highlighted suggestion" instead.
   */
  readonly onKeyDown?: (key: string) => boolean;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
  /** Rendered inside the field's own positioning context — the suggestions popover. */
  readonly below?: ReactNode;
  readonly className?: string;
  /**
   * A clear (✕) button inside the input's end, shown while there is anything to clear — every search
   * bar carries one. Escape clears too, *after* the caller's own key handling: a combobox that
   * claims Escape for its list keeps that meaning, and this fires only for the keys nobody claimed.
   * The label is a prop because `ui/` is domain-free — the words live in the caller's catalogue group,
   * the way `Modal` takes `closeLabel`.
   *
   * `onClear` fires for the ✕ **only**, never for Escape or backspace: pressing the button is
   * "undo this search" — a server-side grid navigates back to the unfiltered list on it — while
   * emptying the text by hand is just editing, applied when the reader submits.
   */
  readonly clear?: { readonly label: string; readonly onClear?: () => void };
  /**
   * Rows, which makes it a `<textarea>`. A prop rather than a second component for `combobox`'s reason
   * — the label rule, the `problem` wiring and the styling are the parts worth having once — and it
   * deliberately turns off the two behaviours that stop making sense: Enter inserts a newline instead
   * of submitting, and a combobox over a multi-line value is not a thing this app has.
   *
   * A playlist description is the first caller, and it is a real multi-line value: the
   * instance stores the newlines and hands them back, uncapped, where the *title* truncates at 150
   * bytes.
   */
  readonly multiline?: number;
  /**
   * A short count under the field — "18 / 150". Its own prop rather than reusing `problem` because it
   * is not a problem: `aria-describedby` must not mark the field invalid for a value that is fine.
   */
  readonly hint?: string | null;
}

export function Field({
  id,
  label,
  value,
  onChange,
  onSubmit,
  placeholder,
  hideLabel,
  type = 'text',
  problem,
  autoComplete = 'off',
  spellCheck = false,
  trailing,
  combobox,
  onKeyDown,
  onFocus,
  onBlur,
  below,
  className,
  clear,
  multiline,
  hint,
}: FieldProps) {
  const problemId = `${id}-problem`;
  const hintId = `${id}-hint`;
  const describedBy = [problem != null ? problemId : null, hint == null ? null : hintId]
    .filter((value) => value !== null)
    .join(' ');

  const shared = {
    id,
    className: styles.input,
    value,
    placeholder,
    autoComplete,
    spellCheck,
    'aria-invalid': problem != null ? true : undefined,
    'aria-describedby': describedBy === '' ? undefined : describedBy,
    onFocus,
    onBlur,
  };

  return (
    <div className={cx(styles.field, className)}>
      <label className={hideLabel === true ? styles.labelHidden : styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.row}>
        {multiline === undefined ? (
          <span className={styles.inputWrap}>
            <input
              {...shared}
              className={cx(styles.input, clear !== undefined && styles.inputClearable)}
              type={type}
              role={combobox === undefined ? undefined : 'combobox'}
              aria-expanded={combobox === undefined ? undefined : combobox.expanded}
              aria-controls={combobox === undefined ? undefined : combobox.listId}
              aria-autocomplete={combobox === undefined ? undefined : 'list'}
              aria-activedescendant={combobox?.activeId ?? undefined}
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => {
                // The caller first — a combobox has its own meaning for Enter, and for Escape, and for the
                // two arrow keys. `preventDefault` only when it says it claimed the key, so an unclaimed
                // `↓` still scrolls and Home/End still move the caret.
                if (onKeyDown?.(event.key) === true) {
                  event.preventDefault();
                  return;
                }
                // Escape empties a clearable box — only when the caller declined the key, so the shell's
                // own Escape ladder (close the list first) stays in charge where there is one.
                if (event.key === 'Escape' && clear !== undefined && value !== '') {
                  event.preventDefault();
                  onChange('');
                  return;
                }
                // Enter submits even without a surrounding `<form>` — the header search is a form, the
                // Home opener is a form, and a future filter row may not be.
                if (event.key === 'Enter' && onSubmit !== undefined) {
                  event.preventDefault();
                  onSubmit();
                }
              }}
            />
            {clear === undefined || value === '' ? null : (
              <button
                type="button"
                className={styles.clear}
                aria-label={clear.label}
                title={clear.label}
                /*
                 * `preventDefault` on mousedown, the suggestions' own trick: without it the press blurs
                 * the input before the click lands — which in the header collapses the whole bar, and
                 * everywhere else drops the caret the reader was about to keep typing with.
                 */
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange('');
                  clear.onClear?.();
                }}
              >
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </span>
        ) : (
          <textarea
            {...shared}
            className={cx(styles.input, styles.textarea)}
            rows={multiline}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              // Escape still reaches the caller (a panel closes on it); Enter does not submit, because
              // in a multi-line value it is a newline and stealing it would make the field unusable.
              if (onKeyDown?.(event.key) === true) event.preventDefault();
            }}
          />
        )}
        {trailing}
        {below}
      </div>
      {problem == null ? null : (
        <p className={styles.problem} id={problemId}>
          {problem}
        </p>
      )}
      {hint == null ? null : (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}
